import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Theme,
  ThemeProvider,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridSlots,
  GridRowSpacingParams,
  GridPagination,
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector,
} from '@mui/x-data-grid-pro';
import { Refresh } from '@mui/icons-material';
import { brokerSharingTheme } from '#app/theme';
import { publishFSCustomEvent } from '#app/fullstory';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomNoRowsOverlay from './CustomNoRows';
import {
  CellComponent,
  ChipsCellComponent,
  CustomToolbar,
} from './TableComponents';
import { differenceInMonths } from 'date-fns';
import { BROKER_NAMES, FilterData } from './utils';
import FilterSection from './FilterSection';
import { GridProSlotProps } from '@mui/x-data-grid-pro/models/gridProSlotProps';
import MuiPagination from '@mui/material/Pagination';
import { TablePaginationProps } from '@mui/material/TablePagination';

// Pagination Component
function Pagination({
  page,
  onPageChange,
  className,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color="secondary"
      shape="rounded"
      className={className}
      count={pageCount}
      showFirstButton
      showLastButton
      page={page + 1}
      size="small"
      onChange={(event, newPage) => {
        onPageChange(event as any, newPage - 1);
      }}
      sx={{
        '& .MuiPaginationItem-root': {
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'Urbanist',
        },
      }}
    />
  );
}

function CustomPagination(props: any) {
  return (
    <GridPagination
      ActionsComponent={Pagination}
      {...props}
      labelDisplayedRows={() => ``}
      labelRowsPerPage="Show per page:"
      rowsPerPageOptions={[5, 10, 25, 100]}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& .MuiTablePagination-selectLabel': {
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'Urbanist',
        },
        '& .MuiTablePagination-select': {
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'Urbanist',
        },
      }}
    />
  );
}

// Order of columns in the data grid
const order = [
  'mcNumber',
  'carrierName',
  'carrierHomeCity',
  'carrierHomeState',
  'carrierPrimaryEquipment',
  'carrierOwner',
  'searchDate',
  'pickupDate',
  'pickupCity',
  'pickupState',
  'dropOffCity',
  'dropOffState',
  'deadheadMiles',
  'maxWeight',
  'equipmentSearched',
  'carrierEmail',
  'carrierPhone',
  'carrierAuthorityAge',
  'rateConUploaded',
];

// Slots for the data grid
const DATA_GRID_SLOTS: Partial<GridSlots> = {
  toolbar: CustomToolbar,
  loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
  noRowsOverlay: CustomNoRowsOverlay,
  pagination: CustomPagination,
};

/* STYLES */
export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    blurredText: {
      filter: 'blur(5px)',
    },
    link: {
      color: 'black',
      wordWrap: 'break-word',
    },
  })
);

const styles = {
  globalFont: {
    '*': {
      fontFamily: 'Urbanist',
    },
  },
  headerText: {
    fontSize: '20px',
    fontWeight: 600,
    fontFamily: 'Urbanist',
    color: '#0D0D12',
    lineHeight: '27px',
  },
  dataGridContainer: {
    height: 1000,
    marginBottom: 10,
    width: '100%',
  },
  dataGrid: {
    borderWidth: 0,
    // Row styling
    '& .MuiDataGrid-row': {
      border: '1px solid #DFE1E6',
      borderRadius: '12px',
      boxShadow: '0px 2px 4px -1px rgba(13, 13, 18, 0.06)',
    },
    // Cell styling
    '.super-app-theme--cell': {
      border: 0,
      alignContent: 'center',
      fontSize: '14px',
      fontWeight: 500,
    },
    // Column header styling
    '& .MuiDataGrid-columnHeaders': {
      border: '1px solid #EEEFF2',
      backgroundColor: '#F8F9FB',
      paddingY: '2px',
      borderRadius: '12px',
      boxShadow: '0px 2px 4px -1px rgba(13, 13, 18, 0.06)',
      'div[role="row"]': {
        backgroundColor: '#F8F9FB',
        borderRadius: '12px',
        '& .MuiDataGrid-filler': {
          backgroundColor: '#F8F9FB',
          borderRadius: '12px',
        },
      },
    },
    // Column cell styling
    '& .MuiDataGrid-columnHeader': {
      bgcolor: '#F8F9FB',
      color: '#666D80',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      border: 0,
    },
    '& .MuiDataGrid-topContainer::after': {
      height: 0,
    },
  },
  dataGridPanel: {
    '& .MuiPaper-rounded': {
      borderRadius: '12px',
    },
    '& .MuiDataGrid-filterFormLogicOperatorInput': { mr: 2 },
    '& .MuiDataGrid-filterFormColumnInput': {
      mr: 2,
      width: 150,
    },
    '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
  },
  secondaryFiltersContainer: {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  refreshButton: {
    borderRadius: '12px',
    height: 50,
    px: '20px',
    textTransform: 'none',
    border: '1px solid #DFE1E6',
    backgroundColor: '#FFFFFF',
    color: '#666D80',
    fontWeight: 500,
    fontSize: '16px',
    fontFamily: 'Urbanist',
    boxShadow: '0px 1px 2px 0px rgba(13, 13, 18, 0.06)',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      color: '#666D80',
    },
  },
  fullWidth: { width: '100%' },
  checkbox: {
    color: '#666D80',
    '&.Mui-checked': {
      color: '#131C7A',
    },
  },
  checkboxContainer: {
    color: '#666D80',
    '& .MuiTypography-root': {
      fontWeight: 500,
      fontSize: '16px',
      fontFamily: 'Urbanist',
    },
  },
  refreshIcon: {
    color: '#666D80',
  },
};

interface Props {}
/* MAIN COMPONENT */
function BrokerSharing(props: Props) {
  /* HOOKS */
  const navigate = useNavigate();
  const classes = useStyles();
  const location = useLocation();
  const url = location.pathname;
  const params = new URLSearchParams(location.search);
  const brokerName = params.get('broker');

  /* STATE */
  const [loading, setLoading] = React.useState(false);
  const [rawData, setRawData] = React.useState<any[]>([]);
  const [daysOfData] = React.useState('3');
  const [showOnlyLastSearch, setShowOnlyLastSearch] = React.useState(false);
  const [clearFilters, setClearFilters] = React.useState(false);

  /* FUNCTIONS */
  /**
   * Formats data and sets it
   */
  const rows = React.useMemo(() => {
    // Format data for rows
    const formattedData = rawData.map((item: any, index: number) => {
      const obj: { [key: string]: any } = {};
      for (let key in item) {
        obj[key] = item[key].value;
      }
      obj.id = index;

      // searchDate should be formatted as MM/DD/YYYY in EST
      const searchDate = new Date(obj.searchDate);
      const offset = searchDate.getTimezoneOffset();
      searchDate.setMinutes(searchDate.getMinutes() - offset);

      // Extract the date parts
      let year = searchDate.getUTCFullYear();
      let month = searchDate.getUTCMonth() + 1; // Months are 0-based in JavaScript
      let day = searchDate.getUTCDate();

      // Format the date
      obj.searchDate = `${('0' + month).slice(-2)}/${('0' + day).slice(
        -2
      )}/${year}`;

      // Take only the date part for pickupDate as a string
      // Extract the date parts from the string
      [year, month, day] = obj.pickupDate.split('T')[0].split('-').map(Number);

      // Format the date
      const formattedDate = `${('0' + month).slice(-2)}/${('0' + day).slice(
        -2
      )}/${year}`;
      obj.pickupDate = formattedDate;

      // Calculate the carrierAuthorityAge in months
      if (obj.carrierAuthorityAge) {
        const months = differenceInMonths(
          new Date(),
          new Date(obj.carrierAuthorityAge)
        );
        obj.carrierAuthorityAge = `${months} month${months !== 1 ? 's' : ''}`;
      }

      obj.deadheadMiles = Number(obj.deadheadMiles).toLocaleString();
      obj.maxWeight = obj.maxWeight
        ? Number(obj.maxWeight).toLocaleString()
        : '';
      return obj;
    });

    return formattedData;
  }, [rawData]);

  const filteredRows = React.useMemo(() => {
    // Filter only the last search for each carrier email
    if (showOnlyLastSearch) {
      const seen = new Set();
      return rows.filter((item: any) => {
        if (seen.has(item.carrierEmail)) {
          return false;
        } else {
          seen.add(item.carrierEmail);
          return true;
        }
      });
    }

    return rows;
  }, [rows, showOnlyLastSearch]);

  const columns = React.useMemo(() => {
    // Get column data from the first object in the array
    const dataObj = rawData[0];
    // If there is no data, return an empty array
    if (!dataObj) {
      return [];
    }

    // Format column data for columns
    const formattedColumnData: GridColDef[] = Object.keys(dataObj).map(
      (key) => {
        const obj: GridColDef = {
          field: key,
          headerName: dataObj[key].header,
          headerClassName: 'super-app-theme--header',
          cellClassName: 'super-app-theme--cell',
          // Set default width
          width: 150,
          // Set default value
          valueGetter: (value) => value || '-',
        };
        if (key === 'carrierEmail') {
          obj.width = 260;
          obj.renderCell = (params) => (
            <CellComponent value={params.value} type="email" />
          );
        } else if (key === 'carrierPhone') {
          obj.renderCell = (params) => (
            <CellComponent value={params.value} type="phone" />
          );
        } else if (key === 'equipmentSearched') {
          obj.width = 300;
          obj.renderCell = (params) => {
            return <ChipsCellComponent value={params.value} />;
          };
        } else if (key === 'carrierPrimaryEquipment') {
          obj.renderCell = (params) => {
            return <ChipsCellComponent value={params.value} />;
          };
        } else if (key === 'rateConUploaded') {
          obj.renderCell = (params) => {
            return params.value === true ? '✅' : '-';
          };
        } else if (
          key === 'searchDate' ||
          key === 'pickupDate' ||
          key === 'mcNumber' ||
          key === 'carrierHomeState' ||
          key === 'pickupState' ||
          key === 'dropOffState'
        ) {
          obj.width = 120;
        } else if (key === 'carrierName') {
          obj.width = 200;
        }

        if (key === 'dropOffCity' || key === 'dropOffState') {
          obj.valueGetter = (value, row) => {
            if (row.dropOffCity === null && row.dropOffState === null) {
              return 'Anywhere';
            }
            return value || '-';
          };
        }

        return obj;
      }
    );

    // Sort the columns based on the order array defined above
    formattedColumnData.sort((a, b) => {
      const indexA = order.indexOf(a.field);
      const indexB = order.indexOf(b.field);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB; // normal sorting
    });

    return formattedColumnData;
  }, [rawData]);

  const slotProps: GridProSlotProps = {
    panel: {
      sx: styles.dataGridPanel,
    },
    footer: {
      sx: {
        border: 0,
      },
    },
  };

  /**
   * Fetches carrier data
   */
  const fetchCarrierData = React.useCallback(
    async (filters: FilterData | undefined = undefined) => {
      let URL = `/api/broker_sharing/carriers?days=${daysOfData}`;

      // Filter data
      const pickUpLat = filters?.pickupLatitude;
      const pickUpLong = filters?.pickupLongitude;
      const pickUpRadius = filters?.pickupRadius;
      const dropOffLat = filters?.dropoffLatitude;
      const dropOffLong = filters?.dropoffLongitude;
      const dropOffRadius = filters?.dropoffRadius;
      const pickUpDate = filters?.pickupDate;

      // Add filter data to the URL if passed in
      if (filters) {
        URL += `&pickup_latitude=${encodeURIComponent(
          pickUpLat ?? ''
        )}&pickup_longitude=${encodeURIComponent(
          pickUpLong ?? ''
        )}&pickup_radius=${encodeURIComponent(
          pickUpRadius ?? ''
        )}&dropoff_latitude=${encodeURIComponent(
          dropOffLat ?? ''
        )}&dropoff_longitude=${encodeURIComponent(
          dropOffLong ?? ''
        )}&dropoff_radius=${encodeURIComponent(
          dropOffRadius ?? ''
        )}&pickup_date=${encodeURIComponent(pickUpDate ?? '')}`;
      }

      try {
        setLoading(true);
        const response = await fetch(URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        const dataArray = data?.data;

        if (!dataArray) {
          console.error('Error fetching carrier data');
          return;
        }

        // Set raw data
        setRawData(dataArray);
      } catch (error) {
        console.error('Error fetching carrier data:', error);
      } finally {
        setLoading(false);
      }
    },
    [daysOfData]
  );

  /**
   * Handle checkbox change event
   */
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyLastSearch(event.target.checked);
  };

  /**
   * Handle cell click event for phone and email
   */
  const onCellClick = (cell: GridCellParams) => {
    if (cell.field !== 'carrierPhone' && cell.field !== 'carrierEmail') {
      return;
    }

    const eventName =
      cell.field === 'carrierPhone'
        ? 'CarrierPhoneClicked'
        : 'CarrierEmailClicked';

    const eventProperties = {
      ...cell.row,
      url,
      broker: brokerName,
    };

    // Send FullStory event when phone or email is clicked
    publishFSCustomEvent(eventName, eventProperties);
  };

  // Controls spacing between rows
  const getRowSpacing = React.useCallback((params: GridRowSpacingParams) => {
    return {
      top: params.isFirstVisible ? 15 : 5,
      bottom: params.isLastVisible ? 20 : 5,
    };
  }, []);

  /**
   * Handle reset
   */
  const handleReset = React.useCallback(() => {
    setClearFilters((prev) => !prev);
    fetchCarrierData();
  }, [fetchCarrierData]);

  /**
   * Handle filters
   */
  const handleFilters = React.useCallback(
    (filterData: FilterData) => {
      // Fetch carrier data with filter data
      fetchCarrierData(filterData);
    },
    [fetchCarrierData]
  );

  /* EFFECTS */
  // Fetch carrier data on mount
  React.useEffect(() => {
    fetchCarrierData();
  }, [fetchCarrierData]);

  // Set document title on mount
  React.useEffect(() => {
    document.title = 'Carrier Data | TrueNorth';
  }, []);

  // Redirect to 404 page if broker name is not provided in the URL
  // or doesn't match the approved broker names
  React.useEffect(() => {
    if (!brokerName || !BROKER_NAMES.includes(brokerName)) {
      navigate('/registration/404');
    }
  }, [brokerName, navigate]);

  /* RENDER */
  return (
    <ThemeProvider theme={brokerSharingTheme}>
      <Box
        className={classes.wrapper}
        display="flex"
        px={{ xs: 2, md: 4, lg: 6 }}
        py={2}
        flexDirection={'column'}
        sx={styles.globalFont}>
        <Box display={'flex'} flexDirection={'row'}>
          <img src={require('#app/icons/TNLogo.svg').default} alt="TNLogo" />
          <h3 style={styles.headerText}>Real-Time Carrier Data</h3>
        </Box>
        <Divider />

        {/* Filters Section */}
        <FilterSection
          onPress={handleFilters}
          isLoading={loading}
          clear={clearFilters}
        />

        <Box style={styles.fullWidth}>
          {/* Secondary Filters Section */}
          <Box py={4}>
            <Box sx={styles.secondaryFiltersContainer} gap={4}>
              <Button
                variant="contained"
                sx={styles.refreshButton}
                startIcon={<Refresh sx={styles.refreshIcon} />}
                disabled={loading}
                onClick={handleReset}>
                Reset
              </Button>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={showOnlyLastSearch}
                      onChange={handleCheckboxChange}
                      sx={styles.checkbox}
                    />
                  }
                  label="Show Recent Searches Only"
                  sx={styles.checkboxContainer}
                />
              </FormGroup>
            </Box>
          </Box>

          {/* DataGrid */}
          <Box sx={styles.dataGridContainer}>
            <DataGridPro
              rowHeight={80}
              getRowSpacing={getRowSpacing}
              onCellClick={onCellClick}
              sx={styles.dataGrid}
              rowSpacingType="margin"
              rows={filteredRows}
              columns={columns}
              slots={DATA_GRID_SLOTS}
              slotProps={slotProps}
              loading={loading}
              pagination={true}
              disableColumnPinning={true}
              autoHeight={true}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
            />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default BrokerSharing;
